import React from 'react';
import {
  Box,
  Checkbox,
  IconButton,
  TextField,
  MenuItem,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import EscalatorWarningIcon from '@mui/icons-material/EscalatorWarning';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import { ReferralData, DeduplicationMatch } from '../types';
import { STATUS } from '../../LeadManagement/constants';
import { formatDateCell } from '../utils';
export interface ColumnConfig {
  id: string;
  label: string;
  accessor: keyof ReferralData | string;
  sortable?: boolean;
  width?: string;
  maxWidth?: string;
  align?: 'left' | 'right' | 'center';
  dropDown?: {
    label: string;
    value: string;
  }[];
}

interface RenderCellValueProps {
  column: ColumnConfig;
  row: ReferralData;
  rowOpen: boolean;
  setRowOpen: (open: boolean) => void;
  enableExpandableRows: boolean;
  onSelectRow: (id: string) => void;
  isCheckboxDisabled: boolean;
  editedCCId: string;
  setEditedCCId: (value: string) => void;
  statusValue: string;
  setStatusValue: (value: string) => void;
  ccIdError: string;
  setCcIdError: (error: string) => void;
  validateCCId: (ccId: string, status: string) => string;
  onPatientCCIdChange?: (id: string, newValue: string) => void;
  onStatusChange?: (id: string, newValue: string) => void;
  currentUser: string;
  lockedRecordsCount: number;
  onLockToggle?: (id: string) => void;
  lockLoadingLeadId?: string | null;
  onOpenMergeModal?: (match: DeduplicationMatch | undefined, row: ReferralData) => void;
  hideSendToCCButton?: boolean;
  dateFormat?: string;
  dateTimeFormat?: string;
}

export const renderCellValue = (props: RenderCellValueProps): React.ReactNode => {
  const {
    column,
    row,
    rowOpen,
    setRowOpen,
    enableExpandableRows,
    onSelectRow,
    isCheckboxDisabled,
    // editedCCId,
    // setEditedCCId,
    statusValue,
    setStatusValue,
    // ccIdError,
    // setCcIdError,
    // validateCCId,
    // onPatientCCIdChange,
    onStatusChange,
    currentUser,
    lockedRecordsCount,
    onLockToggle,
    lockLoadingLeadId,
    hideSendToCCButton,
    dateFormat = 'MM/DD/YYYY',
    dateTimeFormat = 'MM/DD/YYYY HH:MM:SS',
  } = props;

  if (column.id === 'registrationDateTime') {
    return formatDateCell(row.registrationDateTime, dateTimeFormat);
  }

  if (column.id === 'processedDateTime') {
    return formatDateCell(row.processedDateTime, dateTimeFormat);
  }

  if (column.id === 'dob') {
    return formatDateCell(row.dob, dateFormat);
  }

  if (column.id === 'matchPercentage') {
    const capped = Math.min(Number(row.matchPercentage) || 0, 100);
    return `${capped}%`;
  }

  if (column.id === 'expand') {
    const hasExpandableData = true;

    return enableExpandableRows && hasExpandableData ? (
      <IconButton
        aria-label="expand row"
        size="small"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setRowOpen(!rowOpen);
        }}
      >
        {rowOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      </IconButton>
    ) : null;
  }

  if (column.id === 'lock') {
    // Flagged records cannot be locked — show a faded icon to indicate "not available"
    if (row.isFlaggedRecord) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Tooltip title="Locking is not available for flagged records" arrow>
            <LockOpenIcon sx={{ fontSize: 20, opacity: 0.2, color: '#999' }} />
          </Tooltip>
        </Box>
      );
    }

    const isLockedByCurrentUser = row.lockedBy === currentUser;
    const isLockedByOther = row.lockedBy && row.lockedBy !== currentUser;
    const maxLocks = 1;
    const canLock = !row.lockedBy && lockedRecordsCount < maxLocks;
    const rowLeadId = String(row.leadId ?? row.id);
    const isLoading = lockLoadingLeadId != null && lockLoadingLeadId === rowLeadId;
    // Disable all other lock icons while any lock request is in progress
    const isAnotherLockInProgress = lockLoadingLeadId != null && lockLoadingLeadId !== rowLeadId;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <IconButton
          size="small"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            if (onLockToggle && !isLoading && !isAnotherLockInProgress) {
              onLockToggle(rowLeadId);
            }
          }}
          disabled={!!isLockedByOther || isLoading || isAnotherLockInProgress}
          sx={{
            color: isLockedByCurrentUser ? '#f44336' : '#666',
            transition: 'color 0.2s ease',
            '&.Mui-disabled': {
              color: isLockedByCurrentUser ? '#f44336' : '#666',
              opacity: 0.5,
            },
            '&:not(.Mui-disabled):hover': {
              color: '#D32F2F',
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              '& .lock-icon-closed': {
                display: 'inline-flex',
              },
              '& .lock-icon-open': {
                display: 'none',
              },
            },
          }}
          title={
            isLoading
              ? 'Loading...'
              : isLockedByOther
              ? `Locked by ${row.lockedBy}. Lead ID: ${rowLeadId}`
              : isAnotherLockInProgress
              ? 'Another lock action is in progress. Please wait.'
              : isLockedByCurrentUser
              ? 'Click to unlock (You have locked this record)'
              : !canLock
              ? 'You have reached the max number of locks available. To know the items locked by you please go to the filters section and click Locked by me'
              : 'Click to lock this record'
          }
        >
          {isLoading ? (
            <CircularProgress size={20} sx={{ color: 'inherit' }} />
          ) : row.lockedBy ? (
            <LockIcon sx={{ fontSize: 20 }} />
          ) : (
            <>
              <LockOpenIcon className="lock-icon-open" sx={{ fontSize: 20 }} />
              <LockIcon className="lock-icon-closed" sx={{ fontSize: 20, display: 'none' }} />
            </>
          )}
        </IconButton>
      </Box>
    );
  }

  if (column.id === 'patientType') {
    const isMinor = row.patientType === 'minor';
    const isReturningUser = row.dedupeStatus === 'Returning-User';

    // Show icons based on conditions
    if (isMinor || isReturningUser) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
          {isMinor && (
            <Tooltip title="Minor Patient" arrow placement="top">
              <EscalatorWarningIcon
                sx={{
                  fontSize: 20,
                  color: '#3F4040',
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          )}
          {isReturningUser && (
            <Tooltip title="Returning Patient" arrow placement="top">
              <SettingsBackupRestoreIcon
                sx={{
                  fontSize: 20,
                  color: '#3F4040',
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          )}
        </Box>
      );
    }
    return null;
  }

  if (column.id === 'select') {
    // Hide checkbox for auto-merge status records
    if (row.status === STATUS.AUTO_MERGE) {
      return null;
    }

    return (
      <Checkbox
        checked={row.selected}
        onChange={() => onSelectRow(row.id)}
        onClick={(e) => e.stopPropagation()}
        disabled={isCheckboxDisabled}
        sx={{
          color: (theme: any) => theme.palette.neutral5?.main,
          '&.Mui-checked': {
            color: (theme: any) => theme.palette.themeColor?.main,
          },
          '&.Mui-disabled': {
            color: (theme: any) => theme.palette.neutral4?.main,
          },
        }}
      />
    );
  }

  if ((column.dropDown?.length || 0) > 0) {
    const options = column.dropDown;
    const isLockedByOther = !!(row.lockedBy && row.lockedBy !== currentUser);
    return (
      <TextField
        select
        value={statusValue}
        onChange={(e) => {
          const newValue = e.target.value;
          setStatusValue(newValue);

          // If status changes to "New-Record", clear the CC Patient ID
          if (newValue === STATUS.NEW_RECORD) {
            if (onStatusChange) {
              onStatusChange(row.id, newValue);
            }
          } else {
            // Validate CC ID with new status
            // const error = validateCCId(editedCCId, newValue);
            // setCcIdError(error);
            if (onStatusChange) {
              onStatusChange(row.id, newValue);
            }
          }
        }}
        onClick={(e) => e.stopPropagation()}
        size="small"
        fullWidth
        disabled={isLockedByOther || hideSendToCCButton}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontSize: '13px',
            '&:hover fieldset': {
              borderColor: (theme: any) => theme.palette.themeColor?.main,
            },
            '&.Mui-focused fieldset': {
              borderColor: (theme: any) => theme.palette.themeColor?.main,
            },
          },
        }}
      >
        {options?.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  // Editable Patient CC ID field
  // if (column.id === 'patientCCId') {
  //   const isLockedByOther = !!(row.lockedBy && row.lockedBy !== currentUser);
  //   const isDisabled =
  //     statusValue === STATUS.NEW_RECORD || statusValue === STATUS.AUTO_MERGE || isLockedByOther;

  //   return (
  //     <Box>
  //       <TextField
  //         value={editedCCId}
  //         onChange={(e) => {
  //           const newValue = e.target.value;
  //           setEditedCCId(newValue);
  //           // Validate on change
  //           const error = validateCCId(newValue, statusValue);
  //           setCcIdError(error);
  //         }}
  //         onBlur={() => {
  //           const error = validateCCId(editedCCId, statusValue);
  //           setCcIdError(error);

  //           if (!error && onPatientCCIdChange && editedCCId !== row.patientCCId) {
  //             onPatientCCIdChange(row.id, editedCCId);
  //           }
  //         }}
  //         onKeyDown={(e) => {
  //           if (e.key === 'Enter') {
  //             const error = validateCCId(editedCCId, statusValue);
  //             setCcIdError(error);

  //             if (!error && onPatientCCIdChange && editedCCId !== row.patientCCId) {
  //               onPatientCCIdChange(row.id, editedCCId);
  //             }
  //           }
  //           if (e.key === 'Escape') {
  //             setEditedCCId(row.patientCCId);
  //             setCcIdError('');
  //           }
  //         }}
  //         onClick={(e) => e.stopPropagation()}
  //         size="small"
  //         disabled={isDisabled}
  //         error={!!ccIdError}
  //         sx={{
  //           '& .MuiOutlinedInput-root': {
  //             fontSize: '13px',
  //             '&:hover fieldset': {
  //               borderColor: (theme: any) =>
  //                 ccIdError ? theme.palette.error?.main : theme.palette.themeColor?.main,
  //             },
  //             '&.Mui-focused fieldset': {
  //               borderColor: (theme: any) =>
  //                 ccIdError ? theme.palette.error?.main : theme.palette.themeColor?.main,
  //             },
  //             '&.Mui-error fieldset': {
  //               borderColor: (theme: any) => theme.palette.error?.main,
  //             },
  //           },
  //         }}
  //       />
  //       {ccIdError && (
  //         <Typography
  //           variant="caption"
  //           sx={{
  //             color: (theme: any) => theme.palette.error?.main,
  //             fontSize: '10px',
  //             display: 'block',
  //             mt: 0.25,
  //           }}
  //         >
  //           {ccIdError}
  //         </Typography>
  //       )}
  //     </Box>
  //   );
  // }

  const value = row[column.accessor as keyof ReferralData];
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  return (value as string) || '-';
};
